import { useState } from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { CalendarCheck, Clock, User } from "lucide-react";
import { parseApiDate } from "@/utils/dateFormatters";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useCancelClassMutation, useEnrollClassMutation } from "@/mutations/useEnrollClassMutation";
import type { Course, CourseClass } from "@/@types/courses.types";

interface CourseCardProps {
  course: Course;
  classInstance: CourseClass;
  ticketId?: string | null;
}

const formatTime = (startDate: string): string => {
  const d = parseApiDate(startDate);
  return d ? format(d, "HH:mm") : startDate;
};

const formatFullDate = (startDate: string): string => {
  const d = parseApiDate(startDate);
  return d ? format(d, "EEEEEE d MMM, HH:mm", { locale: ru }) : startDate;
};

const CourseCard = ({ course, classInstance, ticketId = null }: CourseCardProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const enrollMutation = useEnrollClassMutation();
  const cancelMutation = useCancelClassMutation();

  const booked = classInstance.booked;
  const capacity = course.capacity;
  const available = Math.max(0, capacity - booked);
  const hasSlots = available > 0;
  const isBooked = Boolean(classInstance.already_booked);

  const trainerName = classInstance.employee?.name || course.employee.name || "";
  const time = formatTime(classInstance.start_date);

  const actionLabel = isBooked ? "Отменить запись" : hasSlots ? "Записаться" : "Мест нет";
  const actionDisabled = !isBooked && !hasSlots;

  const handleAction = () => {
    if (isBooked) setCancelDialogOpen(true);
    else setDialogOpen(true);
  };

  const handleConfirmEnroll = () => {
    enrollMutation.mutate(
      { appointmentId: classInstance.appointment_id, ticketId },
      { onSuccess: () => setDialogOpen(false) }
    );
  };

  const handleConfirmCancel = () => {
    cancelMutation.mutate(
      { appointmentId: classInstance.appointment_id },
      { onSuccess: () => setCancelDialogOpen(false) }
    );
  };

  // Показываем счётчик только если осталось 1 или 2 места — иначе не отвлекаем.
  // «Мест нет» сообщает кнопка, когда available === 0.
  const showRemaining = available === 1 || available === 2;
  const remainingNoun = available === 1 ? "место" : "места";

  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-base font-semibold text-gray-900">{course.title}</span>
        {showRemaining && (
          <span className="whitespace-nowrap text-xs font-medium text-amber-700">
            осталось {available} {remainingNoun}
          </span>
        )}
        {isBooked && (
          <Badge variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]">
            <CalendarCheck className="h-3 w-3" />
            записан
          </Badge>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-800">
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-primary" />
          <span className="font-medium tabular-nums">{time}</span>
        </div>
        {trainerName && (
          <div className="flex items-center gap-1.5">
            <User className="h-4 w-4 text-primary" />
            <span>{trainerName}</span>
          </div>
        )}
      </div>

      <Button
        size="default"
        variant={isBooked ? "outline" : "default"}
        className="mt-3 h-10 w-full"
        onClick={handleAction}
        disabled={actionDisabled}
      >
        {actionLabel}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Запись на занятие</DialogTitle>
            <DialogDescription>
              «{course.title}» — {formatFullDate(classInstance.start_date)}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={enrollMutation.isPending}
            >
              Отмена
            </Button>
            <Button onClick={handleConfirmEnroll} disabled={enrollMutation.isPending}>
              {enrollMutation.isPending ? "Записываем…" : "Записаться"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Отмена записи</DialogTitle>
            <DialogDescription>
              Отменить запись на «{course.title}» — {formatFullDate(classInstance.start_date)}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelMutation.isPending}
            >
              Назад
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Отменяем…" : "Отменить запись"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseCard;
